import { useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Upload, X, CheckCircle2, Lightbulb, Bug, AlertCircle, Heart, HelpCircle } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

const CATEGORIES = [
  { key: "oneri", label: "Öneri", Icon: Lightbulb },
  { key: "hata", label: "Hata Bildir", Icon: Bug },
  { key: "sikayet", label: "Şikâyet", Icon: AlertCircle },
  { key: "memnuniyet", label: "Memnuniyet", Icon: Heart },
  { key: "diger", label: "Diğer", Icon: HelpCircle },
];

export default function GeriBildirim() {
  const [cat, setCat] = useState("");
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!cat) { toast.error("Lütfen bir kategori seçin"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/geri-bildirim", { category: cat, message, photos });
      setDone(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  if (done) {
    return (
      <div data-testid="geri-bildirim-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div data-testid="fb-success" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center space-y-3 animate-fade-up">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
            <h1 className="text-xl font-bold text-emerald-200">{done.message}</h1>
            <p className="text-sm text-slate-400">Kayıt numaranız: <span data-testid="fb-record-no" className="font-bold text-cyan-300">{done.record_no}</span></p>
            <p className="text-xs text-slate-500">Geri bildiriminiz ekibimiz tarafından incelenecektir. Teşekkür ederiz.</p>
            <button data-testid="fb-new-button" onClick={() => { setDone(null); setCat(""); setMessage(""); setPhotos([]); }}
              className="mt-2 px-6 py-2.5 rounded-xl bg-cyan-400 text-slate-950 text-sm font-bold hover:bg-cyan-300 transition-colors">
              Yeni Geri Bildirim Gönder
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="geri-bildirim-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Geri Bildirim</h1>
        </div>
        <p className="text-sm text-slate-400">Öneri, hata, şikâyet veya memnuniyetinizi bizimle paylaşın; her bildirim kayıt numarasıyla takip edilir.</p>

        <form onSubmit={submit} data-testid="fb-form"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-5">
          <div>
            <label className={labelCls}>Kategori *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {CATEGORIES.map((c) => (
                <button key={c.key} type="button" data-testid={`fb-cat-${c.key}`}
                  onClick={() => setCat(c.key)}
                  className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-colors ${
                    cat === c.key ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/30 hover:bg-slate-800/50"}`}>
                  <c.Icon className={`w-4 h-4 shrink-0 ${cat === c.key ? "text-cyan-300" : "text-slate-400"}`} />
                  <span className={`text-xs font-semibold ${cat === c.key ? "text-cyan-200" : "text-slate-300"}`}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Mesajınız *</label>
            <textarea data-testid="fb-message" required rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
              className={inputCls} placeholder="Deneyiminizi, önerinizi veya karşılaştığınız sorunu detaylı yazın..." />
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={async (e) => {
                setUploading(true);
                try {
                  const paths = await uploadFiles([...e.target.files]);
                  setPhotos((p) => [...p, ...paths].slice(0, 4));
                } catch (err) { toast.error(formatApiError(err)); }
                setUploading(false);
                e.target.value = "";
              }} />
            <button type="button" data-testid="fb-photo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Ekran Görüntüsü / Fotoğraf Ekle"}
            </button>
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((p) => (
                  <div key={p} className="relative">
                    <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                    <button type="button" onClick={() => setPhotos(photos.filter((x) => x !== p))}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button data-testid="fb-submit" type="submit" disabled={sending || uploading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all disabled:opacity-50">
            <Send className="w-4 h-4" /> {sending ? "Gönderiliyor..." : "Geri Bildirimi Gönder"}
          </button>
        </form>
      </div>
    </div>
  );
}
